import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { useIsAdmin } from '@/hooks/useIsAdmin';
import {
  PLAN_PREVIEW_LABELS,
  readPlanPreviewMode,
  writePlanPreviewMode,
  type PlanPreviewMode,
} from '@/lib/preview/planPreview';

interface PlanPreviewState {
  /** Modo activo; siempre 'off' para quien no es Super Admin verificado. */
  mode: PlanPreviewMode;
  isPreviewing: boolean;
  /** Sólo el Super Admin verificado puede activar la vista demo. */
  canPreview: boolean;
  label: string | null;
  setMode: (mode: PlanPreviewMode) => void;
  exitPreview: () => void;
}

const PlanPreviewContext = createContext<PlanPreviewState>({
  mode: 'off',
  isPreviewing: false,
  canPreview: false,
  label: null,
  setMode: () => undefined,
  exitPreview: () => undefined,
});

export function PlanPreviewProvider({ children }: { children: ReactNode }) {
  const { isAdmin, loading } = useIsAdmin();
  const [mode, setModeState] = useState<PlanPreviewMode>('off');

  useEffect(() => {
    if (loading) return;
    if (!isAdmin) {
      // Defensa: si alguien escribió la clave a mano, se ignora y se limpia.
      writePlanPreviewMode('off', false);
      setModeState('off');
      return;
    }
    setModeState(readPlanPreviewMode(true));
  }, [isAdmin, loading]);

  const setMode = useCallback(
    (next: PlanPreviewMode) => {
      setModeState(writePlanPreviewMode(next, isAdmin));
    },
    [isAdmin],
  );

  const exitPreview = useCallback(() => setMode('off'), [setMode]);

  const value = useMemo<PlanPreviewState>(
    () => ({
      mode,
      isPreviewing: mode !== 'off',
      canPreview: isAdmin,
      label: mode === 'off' ? null : PLAN_PREVIEW_LABELS[mode],
      setMode,
      exitPreview,
    }),
    [mode, isAdmin, setMode, exitPreview],
  );

  return <PlanPreviewContext.Provider value={value}>{children}</PlanPreviewContext.Provider>;
}

export function usePlanPreview(): PlanPreviewState {
  return useContext(PlanPreviewContext);
}
